"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Flame, Zap, ShieldCheck, Wrench, ArrowRight, CircleCheck as CheckCircle2, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const whyChooseCards = [{
  icon: Flame,
  title: "Gas System Expertise",
  description: "Certified technicians trained in safe gas line connections, venting requirements, and pilot ignition safety."
}, {
  icon: Zap,
  title: "Rapid Emergency Diagnosis",
  description: "Quick detection of thermocouple failures, gas valve faults, sediment buildup, and pressure leaks."
}, {
  icon: ShieldCheck,
  title: "Factory-Spec Repairs",
  description: "We install authentic OEM replacement parts to preserve manufacturer warranties and factory efficiency."
}];
const brandCards = [{
  name: "Rheem",
  tagline: "Performance & Reliability",
  description: "Leading residential gas water heaters known for high efficiency and quick recovery rates.",
  features: "Tank & Tankless Models • EcoNet Smart Tech"
}, {
  name: "Bradford White",
  tagline: "Contractor-Grade Durability",
  description: "Engineered with Defender Safety System and Hydrojet Total Performance System for long life.",
  features: "Atmospheric & Power Vent • Built-In Safety"
}, {
  name: "A.O. Smith",
  tagline: "High Efficiency Innovation",
  description: "Commercial and residential grade units with advanced electronic gas control and glass lining.",
  features: "Signature Series • Energy Star Rated"
}, {
  name: "Rinnai",
  tagline: "Continuous Flow Tankless",
  description: "Compact wall-mounted gas tankless water heaters delivering endless on-demand hot water.",
  features: "Ultra-High Efficiency • Space-Saving"
}];
export default function HeaterBrands() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="gaswaterheaterexpertiseandbrands" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            <Flame className="w-3.5 h-3.5 text-accent mr-1" />
            Specialized Gas Plumbing
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Gas Water Heater Expertise &amp; Brands We Service
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            From pilot light outages and faulty gas valves to emergency replacements, our Jonesboro plumbers deliver fast, code-compliant gas water heater services.
          </p>
        </div>

        {/* Why Choose Us Highlight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {whyChooseCards.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.title} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }}>
                <Card className="h-full border border-border shadow-sm bg-card hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="w-12 h-12 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center mb-2">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Brand Showcase Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4 border-b border-border pb-4">
          <div>
            <h3 className="text-2xl font-bold tracking-tight text-foreground font-default">
              Featured Water Heater Brands
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Experienced with all top-tier residential and commercial manufacturer equipment.
            </p>
          </div>
          <div>
            <Button asChild variant="outline" size="sm" className="font-medium">
              <Link to="/brands/" className="inline-flex items-center gap-1.5">
                <span>View All Brands</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Brand Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {brandCards.map((brand, index) => <motion.div key={brand.name} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} className="flex">
              <Card className="w-full flex flex-col justify-between border border-border bg-card shadow-sm hover:border-primary/40 hover:shadow-md transition-all">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <Badge variant="outline" className="text-xs bg-muted/60 text-muted-foreground font-semibold">
                      Authorized Care
                    </Badge>
                    <Wrench className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-card-foreground">
                    {brand.name}
                  </CardTitle>
                  <CardDescription className="text-xs font-medium text-accent-foreground font-sans">
                    {brand.tagline}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 flex-1">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {brand.description}
                  </p>
                  <Separator />
                  <div className="flex items-start gap-2 pt-1">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-xs font-medium text-foreground">
                      {brand.features}
                    </span>
                  </div>
                </CardContent>
                <CardFooter className="pt-2 border-t border-border/60">
                  <Button asChild variant="ghost" size="sm" className="w-full justify-between text-primary hover:text-primary">
                    <Link to="/brands/" className="w-full inline-flex items-center justify-between font-semibold">
                      <span>Brand Details</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom Callout Banner */}
        <div className="mt-14 rounded-2xl bg-secondary/70 border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h4 className="text-lg font-bold text-foreground">
              Have a different water heater brand or need fast repairs?
            </h4>
            <p className="text-sm text-muted-foreground">
              Our 24/7 technicians service all makes, models, tankless units, and traditional gas water heaters.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild variant="default" size="lg" className="shadow-xs font-semibold">
              <Link to="/water-heater-repair/" className="inline-flex items-center gap-2">
                <Wrench className="w-4 h-4" />
                <span>Schedule Repair</span>
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="font-semibold">
              <Link to="/contact/" className="inline-flex items-center gap-2">
                <PhoneCall className="w-4 h-4" />
                <span>Contact Plumber</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}